"use client";

import { useState, useEffect } from "react";
import Image from "next/image";

const navLinks = [
  { label: "Collection", href: "#collection" },
  { label: "Categories", href: "/categories" },
  { label: "Alchemy", href: "#alchemy" },
  { label: "Voices", href: "#voices" },
  { label: "The Ritual", href: "#ritual" },
];

export default function Navigation({ onOrderClick }: { onOrderClick?: () => void }) {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  return (
    <>
      <header
        role="banner"
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
          scrolled
            ? "py-4 nav-glass shadow-[0_10px_30px_rgba(122,100,66,0.08)]"
            : "py-6 bg-transparent"
        }`}
      >
        <nav aria-label="Main navigation" className="max-w-[1400px] mx-auto px-6 md:px-12 flex items-center justify-between">
          {/* Logo */}
          <a href="#" aria-label="STYGIAN — Home" className="relative z-10 flex items-center gap-3 group" data-cursor="pointer">
            <div className="relative w-8 h-8">
              <Image
                src="/images/logo-main.webp"
                alt="STYGIAN Perfumes"
                fill
                className="object-contain"
                sizes="32px"
                priority
                style={{ filter: "drop-shadow(0 2px 6px rgba(168,132,58,0.25))" }}
              />
            </div>
            <div className="flex flex-col">
              <span className="font-serif text-lg tracking-[0.15em] uppercase text-[#221d17]">
                Stygian
              </span>
              <span className="text-[6px] tracking-[0.4em] uppercase text-[#8a6a2c]/60 -mt-0.5">
                Parfums
              </span>
            </div>
          </a>

          {/* Desktop links */}
          <div className="hidden md:flex items-center gap-10">
            {navLinks.map((link) => (
              <a
                key={link.href}
                href={link.href}
                className="relative text-[11px] tracking-[0.15em] uppercase text-[#6f6759]/80 hover:text-[#8a6a2c] transition-colors duration-300 group"
                data-cursor="pointer"
              >
                {link.label}
                <span className="absolute -bottom-1 left-0 w-0 h-px bg-[#c9a84c]/70 transition-all duration-300 group-hover:w-full" />
              </a>
            ))}
          </div>

          {/* Right side */}
          <div className="hidden md:flex items-center gap-6">
            <button
              onClick={onOrderClick}
              className="px-5 py-2 text-[10px] tracking-[0.15em] uppercase text-[#8a6a2c] hover:text-[#6f551f] border border-[#c9a84c]/40 hover:border-[#c9a84c]/70 transition-all duration-300"
              data-cursor="pointer"
            >
              Order
            </button>
            <button
              aria-label="Search"
              className="text-[#6f6759]/60 hover:text-[#8a6a2c] transition-colors duration-300"
              data-cursor="pointer"
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                <circle cx="11" cy="11" r="8" />
                <path d="m21 21-4.35-4.35" />
              </svg>
            </button>
            <button
              aria-label="Shopping cart"
              className="relative text-[#6f6759]/60 hover:text-[#8a6a2c] transition-colors duration-300"
              data-cursor="pointer"
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                <path d="M6 2L3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4z" />
                <line x1="3" y1="6" x2="21" y2="6" />
                <path d="M16 10a4 4 0 0 1-8 0" />
              </svg>
            </button>
          </div>

          {/* Mobile burger */}
          <button
            className="md:hidden relative z-10 w-8 h-8 flex flex-col items-end justify-center gap-1.5"
            onClick={() => setMobileOpen(!mobileOpen)}
            aria-label={mobileOpen ? "Close menu" : "Open menu"}
            aria-expanded={mobileOpen}
          >
            <span
              className={`block h-px bg-[#221d17] transition-all duration-300 ${
                mobileOpen ? "w-6 rotate-45 translate-y-[3.5px]" : "w-6"
              }`}
            />
            <span
              className={`block h-px bg-[#221d17] transition-all duration-300 ${
                mobileOpen ? "w-6 -rotate-45 -translate-y-[3.5px]" : "w-4"
              }`}
            />
          </button>
        </nav>
      </header>

      {/* Mobile menu */}
      <div
        className={`md:hidden fixed inset-0 z-40 transition-all duration-500 flex flex-col items-center justify-center gap-10 ${
          mobileOpen ? "opacity-100 pointer-events-auto" : "opacity-0 pointer-events-none"
        }`}
        style={{ background: "rgba(249,246,240,0.97)" }}
      >
        <div className="relative w-12 h-12 mb-4">
          <Image src="/images/logo-main.webp" alt="STYGIAN" fill className="object-contain" sizes="48px" />
        </div>
        {navLinks.map((link) => (
          <a
            key={link.href}
            href={link.href}
            onClick={() => setMobileOpen(false)}
            className="font-serif text-3xl tracking-[0.1em] uppercase text-[#221d17] hover:text-[#8a6a2c] transition-colors"
          >
            {link.label}
          </a>
        ))}
        <button
          onClick={() => {
            setMobileOpen(false);
            onOrderClick?.();
          }}
          className="mt-4 px-8 py-3 text-[11px] tracking-[0.2em] uppercase text-[#8a6a2c] border border-[#c9a84c]/40 hover:border-[#c9a84c]/70 transition-all duration-300"
        >
          Order Now
        </button>
        <div className="flex gap-8 mt-8">
          <button className="text-[#6f6759]/60 hover:text-[#8a6a2c] transition-colors" aria-label="Search">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
              <circle cx="11" cy="11" r="8" />
              <path d="m21 21-4.35-4.35" />
            </svg>
          </button>
          <button className="text-[#6f6759]/60 hover:text-[#8a6a2c] transition-colors" aria-label="Cart">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
              <path d="M6 2L3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4z" />
              <line x1="3" y1="6" x2="21" y2="6" />
              <path d="M16 10a4 4 0 0 1-8 0" />
            </svg>
          </button>
        </div>
      </div>
    </>
  );
}